import { Injectable } from '@angular/core'

// 'two' = Primary and Recovery are deployed from separate management accounts (suggested default).
// 'one' = a single management account deploys both StackSets.
export type StacksetsManagementTopology = 'one' | 'two'

export interface StacksetsConfig {
  managementTopology: StacksetsManagementTopology
  // Primary management account ID. In topology='one' this is the single management
  // account that owns both StackSets; in topology='two' it owns the Primary StackSet.
  primaryManagementAccountId: string
  // Only meaningful when topology === 'two' (owns the Recovery StackSet). Kept populated
  // across topology toggles so users don't lose it when they switch back and forth.
  recoveryManagementAccountId: string
  primaryStackSetName: string
  recoveryStackSetName: string
}

export function createDefaultStacksetsConfig(): StacksetsConfig {
  return {
    managementTopology: 'two',
    primaryManagementAccountId: '',
    recoveryManagementAccountId: '',
    primaryStackSetName: '',
    recoveryStackSetName: '',
  }
}

const STORAGE_KEY_PREFIX = 'arpio_stacksets_config'
// Convenience cache only. The access API can't tell us which StackSet manages a stack,
// so we keep the user's last-used values around for a while to avoid re-asking on every
// visit. Authoritative state is what's deployed in AWS, not what's in here.
const CONFIG_EXPIRY_HOURS = 24 * 30

interface PersistedConfig extends StacksetsConfig {
  lastUpdated: number
}

/**
 * Per-Arpio-account localStorage cache for StackSets setup inputs.
 *
 * Used by both stacksets-instructions (setup/migration) and stacksets-update-instructions
 * (post-setup updates) so the topology choice, management account IDs, and StackSet
 * names the user entered once get pre-filled across every app in the same Arpio account.
 *
 * StackSets are an account-level architectural choice: one set of management accounts
 * and StackSet names typically owns access for every Arpio app the customer has, so
 * keying per Arpio account avoids re-asking on every new sync pair.
 *
 * Storage is local to the browser; cross-device users will re-enter once.
 */
@Injectable({
  providedIn: 'root',
})
export class StacksetsConfigCacheService {
  load(arpioAccountId: string): StacksetsConfig | null {
    const key = this.buildKey(arpioAccountId)
    if (!key) return null
    try {
      const stored = localStorage.getItem(key)
      if (!stored) return null
      const parsed: PersistedConfig = JSON.parse(stored)
      const hoursOld = (Date.now() - parsed.lastUpdated) / (1000 * 60 * 60)
      if (hoursOld > CONFIG_EXPIRY_HOURS) {
        localStorage.removeItem(key)
        return null
      }
      return {
        managementTopology: parsed.managementTopology === 'one' ? 'one' : 'two',
        primaryManagementAccountId: parsed.primaryManagementAccountId || '',
        recoveryManagementAccountId: parsed.recoveryManagementAccountId || '',
        primaryStackSetName: parsed.primaryStackSetName || '',
        recoveryStackSetName: parsed.recoveryStackSetName || '',
      }
    } catch (e) {
      console.error('Error loading StackSets config cache:', e)
      return null
    }
  }

  save(arpioAccountId: string, config: StacksetsConfig): void {
    const key = this.buildKey(arpioAccountId)
    if (!key) return
    try {
      const payload: PersistedConfig = { ...config, lastUpdated: Date.now() }
      localStorage.setItem(key, JSON.stringify(payload))
    } catch (e) {
      console.error('Error saving StackSets config cache:', e)
    }
  }

  clear(arpioAccountId: string): void {
    const key = this.buildKey(arpioAccountId)
    if (!key) return
    try {
      localStorage.removeItem(key)
    } catch (e) {
      console.error('Error clearing StackSets config cache:', e)
    }
  }

  private buildKey(arpioAccountId: string): string | null {
    if (!arpioAccountId) return null
    return `${STORAGE_KEY_PREFIX}_${arpioAccountId}`
  }
}
